import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useWatchAuth } from '@/hooks/useWatchAuth'
import { api, WebPlayerChannel } from '@/lib/api'
import VideoPlayer from '@/components/VideoPlayer'
import EPGGuide from '@/components/EPGGuide'
import CastPanel from '@/components/CastPanel'
import { useTVScene } from '@/hooks/useTVScene'
import {
  Search,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Play,
  Tv,
  Grid2X2,
  Square,
  Columns2,
  Menu,
  X,
  List,
  Volume2,
} from 'lucide-react'

type LayoutMode = 1 | 2 | 4

interface ActivePlayer {
  channel: WebPlayerChannel
  slot: number
}

// Channel logo color palette (deterministic per channel name)
function getChannelColor(name: string) {
  const palette = [
    { bg: 'oklch(28% 0.10 25)', ink: 'oklch(85% 0.12 25)' },
    { bg: 'oklch(28% 0.10 250)', ink: 'oklch(85% 0.12 250)' },
    { bg: 'oklch(28% 0.10 140)', ink: 'oklch(85% 0.12 140)' },
    { bg: 'oklch(28% 0.10 50)', ink: 'oklch(85% 0.12 50)' },
    { bg: 'oklch(28% 0.10 320)', ink: 'oklch(85% 0.12 320)' },
    { bg: 'oklch(28% 0.10 200)', ink: 'oklch(85% 0.12 200)' },
  ]
  return palette[(name.charCodeAt(0) || 0) % palette.length]
}

export default function Watch() {
  const { isAuthenticated, isLoading, checkAuth, logout, user, getStreamUrl } = useWatchAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [activePlayers, setActivePlayers] = useState<ActivePlayer[]>([])
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(1)
  const [isMobile, setIsMobile] = useState(false)
  const [showGuide, setShowGuide] = useState(false)

  // TV Scene store for casting
  const { setLayout, addTile, removeTile, clearTiles } = useTVScene()

  // Fetch features directly from API
  const { data: features } = useQuery({
    queryKey: ['accountFeatures'],
    queryFn: () => api.getAccountFeatures(),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })

  const mosaicEnabled = features?.mosaic ?? user?.mosaic_enabled ?? false
  const maxMosaicStreams = features?.max_mosaic_streams ?? user?.max_mosaic_streams ?? 1
  const maxLayout: LayoutMode = !mosaicEnabled ? 1 : (maxMosaicStreams >= 4 ? 4 : maxMosaicStreams >= 2 ? 2 : 1)

  useEffect(() => {
    console.log('[Watch] Mosaic debug:', {
      'features?.mosaic': features?.mosaic,
      'user?.mosaic_enabled': user?.mosaic_enabled,
      mosaicEnabled,
      maxMosaicStreams,
      maxLayout,
      layoutMode,
      effectiveLayout: Math.min(layoutMode, maxLayout),
      featuresLoaded: !!features,
      userLoaded: !!user,
    })
  }, [features, user, mosaicEnabled, maxLayout, layoutMode])

  useEffect(() => {
    if (layoutMode > maxLayout) {
      setLayoutMode(maxLayout)
    }
  }, [maxLayout, layoutMode])

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setSidebarOpen(false)
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(window.location.hostname.startsWith('watch.') ? '/login' : '/watch/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  const { data: channels = [], isLoading: channelsLoading, error } = useQuery({
    queryKey: ['webPlayerChannels'],
    queryFn: () => api.getWebPlayerChannels(),
    enabled: isAuthenticated,
    refetchInterval: 60000,
  })

  const categories = useMemo(() => {
    const cats = new Set<string>()
    channels.forEach(ch => {
      if (ch.category) cats.add(ch.category)
    })
    return Array.from(cats).sort()
  }, [channels])

  const filteredChannels = useMemo(() => {
    return channels.filter(ch => {
      const matchesSearch = searchQuery === '' ||
        ch.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = selectedCategory === 'all' ||
        ch.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [channels, searchQuery, selectedCategory])

  const syncTVScene = useCallback((players: ActivePlayer[], layout: LayoutMode) => {
    const layoutMap: Record<LayoutMode, 'single' | 'dual' | 'quad'> = {
      1: 'single',
      2: 'dual',
      4: 'quad',
    }
    setLayout(layoutMap[layout])
    clearTiles()
    players.forEach(p => {
      addTile({
        slot: p.slot,
        channelId: p.channel.id,
        channelName: p.channel.name,
        streamUrl: p.channel.stream_url,
        iconUrl: p.channel.icon_url,
      })
    })
  }, [setLayout, clearTiles, addTile])

  const handleChannelClick = (channel: WebPlayerChannel) => {
    const existingIndex = activePlayers.findIndex(p => p.channel.id === channel.id)
    if (existingIndex >= 0) return

    const maxSlots = Math.min(layoutMode, maxLayout) as LayoutMode
    let newPlayers: ActivePlayer[]

    if (activePlayers.length < maxSlots) {
      const usedSlots = activePlayers.map(p => p.slot)
      const availableSlots = Array.from({ length: maxSlots }, (_, i) => i)
      const nextSlot = availableSlots.find(s => !usedSlots.includes(s)) ?? 0
      newPlayers = [...activePlayers, { channel, slot: nextSlot }]
    } else {
      const trimmedPlayers = [...activePlayers.slice(1)]
      const usedSlots = trimmedPlayers.map(p => p.slot)
      const availableSlots = Array.from({ length: maxSlots }, (_, i) => i)
      const nextSlot = availableSlots.find(s => !usedSlots.includes(s)) ?? 0
      newPlayers = [...trimmedPlayers, { channel, slot: nextSlot }]
    }

    setActivePlayers(newPlayers)
    syncTVScene(newPlayers, layoutMode)

    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const handleClosePlayer = (channelId: string) => {
    const player = activePlayers.find(p => p.channel.id === channelId)
    if (player) {
      const streamUrl = getStreamUrl(player.channel.stream_url)
      if (!streamUrl.includes('{password}')) {
        api.stopPlayback(streamUrl)
      }
      removeTile(player.slot)
    }
    const newPlayers = activePlayers.filter(p => p.channel.id !== channelId)
    setActivePlayers(newPlayers)
  }

  const handleLogout = () => {
    logout()
    navigate(window.location.hostname.startsWith('watch.') ? '/login' : '/watch/login')
  }

  const isChannelPlaying = (channelId: string) => {
    return activePlayers.some(p => p.channel.id === channelId)
  }

  const handleLayoutChange = (newLayout: LayoutMode) => {
    setLayoutMode(newLayout)
    let newPlayers = activePlayers

    if (activePlayers.length > newLayout) {
      const removedPlayers = activePlayers.slice(newLayout)
      removedPlayers.forEach(player => {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (!streamUrl.includes('{password}')) {
          api.stopPlayback(streamUrl)
        }
        removeTile(player.slot)
      })
      newPlayers = activePlayers.slice(0, newLayout)
      setActivePlayers(newPlayers)
    }

    syncTVScene(newPlayers, newLayout)
  }

  // Cleanup: stop all streams when component unmounts or page unloads
  useEffect(() => {
    const stopAllStreams = () => {
      activePlayers.forEach(player => {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (!streamUrl.includes('{password}')) {
          const stopUrl = streamUrl.replace('.m3u8', '/stop')
          navigator.sendBeacon(stopUrl)
        }
      })
    }

    window.addEventListener('beforeunload', stopAllStreams)

    return () => {
      window.removeEventListener('beforeunload', stopAllStreams)
      stopAllStreams()
    }
  }, [activePlayers, getStreamUrl])

  const effectiveLayout = Math.min(layoutMode, maxLayout) as LayoutMode

  const getGridClasses = () => {
    switch (effectiveLayout) {
      case 1:
        return 'grid-cols-1 grid-rows-1'
      case 2:
        return 'grid-cols-1 md:grid-cols-2 grid-rows-2 md:grid-rows-1'
      case 4:
      default:
        return 'grid-cols-2 grid-rows-2'
    }
  }

  // Render mosaic grid slots
  const renderGrid = () => {
    const slots = Array.from({ length: effectiveLayout }, (_, i) => i)
    return slots.map(slot => {
      const player = activePlayers.find(p => p.slot === slot)

      if (player) {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (streamUrl.includes('{password}')) {
          return (
            <div
              key={slot}
              className="flex items-center justify-center relative overflow-hidden rounded-xl"
              style={{
                background: 'var(--gs-bg-1)',
                border: '1px solid var(--gs-glass-stroke)',
              }}
            >
              <div className="text-center">
                <div
                  className="w-8 h-8 rounded-full border-2 border-t-transparent mx-auto mb-2 animate-spin"
                  style={{ borderColor: 'var(--gs-accent)', borderTopColor: 'transparent' }}
                />
                <p className="text-sm" style={{ color: 'var(--gs-ink-2)' }}>Loading...</p>
              </div>
            </div>
          )
        }
        const isAudioSlot = slot === 0
        return (
          <div
            key={slot}
            className="relative overflow-hidden rounded-xl transition-all duration-200"
            style={{
              background: 'var(--gs-bg-0)',
              border: isAudioSlot && effectiveLayout > 1
                ? '2px solid var(--gs-accent)'
                : '1px solid var(--gs-glass-stroke)',
              boxShadow: isAudioSlot && effectiveLayout > 1
                ? '0 0 24px var(--gs-accent-glow)'
                : 'none',
            }}
          >
            <VideoPlayer
              src={streamUrl}
              channelName={player.channel.name}
              channelIcon={player.channel.icon_url}
              onClose={() => handleClosePlayer(player.channel.id)}
              index={activePlayers.indexOf(player)}
            />
            {/* Slot number watermark for mosaic */}
            {effectiveLayout > 1 && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none gs-mono"
                style={{
                  fontSize: 96,
                  fontWeight: 700,
                  color: 'oklch(82% 0.02 250 / 0.04)',
                }}
              >
                {slot + 1}
              </div>
            )}
          </div>
        )
      }

      return (
        <div
          key={slot}
          className="flex items-center justify-center relative overflow-hidden rounded-xl"
          style={{
            background: 'var(--gs-bg-1)',
            border: '1px solid var(--gs-glass-stroke)',
          }}
        >
          <div className="text-center">
            <Tv className="w-16 h-16 mx-auto mb-3" style={{ color: 'var(--gs-ink-3)', opacity: 0.3 }} />
            <p className="text-sm" style={{ color: 'var(--gs-ink-3)', opacity: 0.6 }}>Select a channel</p>
          </div>
        </div>
      )
    })
  }

  if (isLoading) {
    return (
      <div className="gs flex items-center justify-center min-h-screen">
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--gs-accent)', borderTopColor: 'transparent' }}
        />
      </div>
    )
  }

  return (
    <div className="gs flex h-screen overflow-hidden">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-20"
          style={{ background: 'oklch(0% 0 0 / 0.6)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — glass panel */}
      <aside
        className={`flex flex-col z-30 transition-all duration-300 ${
          isMobile
            ? `fixed inset-y-0 left-0 w-80 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            : sidebarOpen ? 'w-80' : 'w-0 overflow-hidden'
        }`}
        style={{
          background: 'var(--gs-bg-0)',
          borderRight: '1px solid var(--gs-glass-stroke)',
        }}
      >
        {/* Header — OBAStream V2 wordmark */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--gs-glass-stroke)' }}
        >
          <div className="flex items-baseline gap-0">
            <span
              className="text-[22px] font-extrabold"
              style={{ letterSpacing: '-0.03em', color: 'var(--gs-ink-0)' }}
            >
              OBA
            </span>
            <span
              className="gs-mono text-[11px] font-semibold uppercase ml-1.5"
              style={{ letterSpacing: '0.18em', color: 'var(--gs-accent)' }}
            >
              stream
            </span>
            <span
              className="gs-mono text-[9px] font-semibold ml-1.5"
              style={{ color: 'var(--gs-ink-3)' }}
            >
              v2
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CastPanel className="hidden sm:flex" />
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg transition-colors cursor-pointer"
              style={{ color: 'var(--gs-ink-2)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg transition-colors cursor-pointer"
                style={{ color: 'var(--gs-ink-2)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Layout Switcher — glass pill buttons */}
        {maxLayout > 1 && (
          <div className="px-4 pt-4 pb-2">
            <div
              className="flex items-center gap-1 p-1 rounded-xl"
              style={{
                background: 'oklch(82% 0.02 250 / 0.06)',
                border: '1px solid var(--gs-glass-stroke)',
              }}
            >
              {([1, 2, 4] as LayoutMode[]).filter(l => l <= maxLayout).map(mode => {
                const Icon = mode === 1 ? Square : mode === 2 ? Columns2 : Grid2X2
                const label = mode === 1 ? '1×1' : mode === 2 ? '2×1' : '2×2'
                const active = layoutMode === mode
                return (
                  <button
                    key={mode}
                    onClick={() => handleLayoutChange(mode)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg gs-mono text-[11px] font-semibold transition-all cursor-pointer"
                    style={{
                      background: active ? 'var(--gs-accent-soft)' : 'transparent',
                      border: `1px solid ${active ? 'var(--gs-accent)' : 'transparent'}`,
                      color: active ? 'var(--gs-accent)' : 'var(--gs-ink-2)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="px-4 pt-3 pb-2 space-y-2.5">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--gs-ink-3)' }}
            />
            <input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl text-sm focus:outline-none transition-all"
              style={{
                background: 'oklch(82% 0.02 250 / 0.06)',
                border: '1px solid var(--gs-glass-stroke)',
                color: 'var(--gs-ink-0)',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Category Filter — pill style */}
          <div className="flex items-center gap-1.5 overflow-x-auto gs-scroll-hide pb-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className="px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all cursor-pointer shrink-0"
              style={{
                background: selectedCategory === 'all' ? 'var(--gs-accent-soft)' : 'oklch(82% 0.02 250 / 0.06)',
                border: `1px solid ${selectedCategory === 'all' ? 'var(--gs-accent)' : 'var(--gs-glass-stroke)'}`,
                color: selectedCategory === 'all' ? 'var(--gs-accent)' : 'var(--gs-ink-1)',
                fontFamily: 'inherit',
              }}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className="px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all cursor-pointer shrink-0"
                style={{
                  background: selectedCategory === cat ? 'var(--gs-accent-soft)' : 'oklch(82% 0.02 250 / 0.06)',
                  border: `1px solid ${selectedCategory === cat ? 'var(--gs-accent)' : 'var(--gs-glass-stroke)'}`,
                  color: selectedCategory === cat ? 'var(--gs-accent)' : 'var(--gs-ink-1)',
                  fontFamily: 'inherit',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Active Players Count */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2">
            <Play className="w-3.5 h-3.5" style={{ color: 'var(--gs-ink-3)' }} />
            <span
              className="gs-mono text-[10px] font-medium"
              style={{ letterSpacing: '0.08em', color: 'var(--gs-ink-3)' }}
            >
              {activePlayers.length}/{layoutMode} STREAMS
            </span>
            {effectiveLayout > 1 && (
              <>
                <span className="w-1 h-1 rounded-full" style={{ background: 'var(--gs-ink-3)' }} />
                <span
                  className="gs-mono text-[10px] font-medium"
                  style={{ letterSpacing: '0.08em', color: 'var(--gs-ink-3)' }}
                >
                  AUDIO SLOT 1
                </span>
              </>
            )}
          </div>
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto px-2 pb-4 gs-scroll-hide">
          {channelsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div
                className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: 'var(--gs-accent)', borderTopColor: 'transparent' }}
              />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-sm" style={{ color: 'oklch(82% 0.18 25)' }}>
              Failed to load channels
            </div>
          ) : filteredChannels.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--gs-ink-3)' }}>
              No channels found
            </div>
          ) : (
            <div className="space-y-1">
              {filteredChannels.map(channel => {
                const isPlaying = isChannelPlaying(channel.id)
                const colors = getChannelColor(channel.name)
                return (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelClick(channel)}
                    disabled={isPlaying}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all cursor-pointer disabled:cursor-default"
                    style={{
                      background: isPlaying ? 'var(--gs-accent-soft)' : 'transparent',
                      border: isPlaying ? '1px solid var(--gs-accent)' : '1px solid transparent',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                      if (!isPlaying) e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.06)'
                    }}
                    onMouseLeave={e => {
                      if (!isPlaying) e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    {channel.icon_url ? (
                      <img
                        src={channel.icon_url}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        style={{
                          border: '1px solid var(--gs-glass-stroke)',
                          background: colors.bg,
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 gs-mono text-[11px] font-bold"
                        style={{
                          background: colors.bg,
                          color: colors.ink,
                          border: '1px solid var(--gs-glass-stroke)',
                        }}
                      >
                        {channel.name.substring(0, 3).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-medium truncate text-[13px]"
                          style={{ color: 'var(--gs-ink-0)' }}
                        >
                          {channel.name}
                        </span>
                        {isPlaying && (
                          <span
                            className="gs-mono inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0"
                            style={{
                              background: 'oklch(70% 0.20 25 / 0.18)',
                              border: '1px solid oklch(70% 0.20 25 / 0.4)',
                              color: 'oklch(82% 0.18 25)',
                              letterSpacing: '0.08em',
                            }}
                          >
                            <span className="gs-live-dot" style={{ width: 5, height: 5 }} />
                            LIVE
                          </span>
                        )}
                      </div>
                      {channel.category && (
                        <span
                          className="gs-mono text-[9px] truncate block"
                          style={{ letterSpacing: '0.08em', color: 'var(--gs-ink-3)' }}
                        >
                          {channel.category.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* User Info */}
        {user && (
          <div
            className="px-5 py-3 text-[11px]"
            style={{
              borderTop: '1px solid var(--gs-glass-stroke)',
              color: 'var(--gs-ink-3)',
            }}
          >
            Logged in as{' '}
            <span style={{ color: 'var(--gs-ink-1)' }}>{user.username}</span>
          </div>
        )}
      </aside>

      {/* Sidebar Toggle — Desktop only */}
      {!isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-1/2 -translate-y-1/2 z-10 p-1 rounded-r-lg transition-all duration-300 cursor-pointer"
          style={{
            left: sidebarOpen ? '320px' : 0,
            background: 'var(--gs-bg-1)',
            border: '1px solid var(--gs-glass-stroke)',
            borderLeft: 'none',
            color: 'var(--gs-ink-2)',
          }}
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Main Content */}
      <main className="flex-1 relative flex flex-col" style={{ background: 'var(--gs-bg-0)' }}>
        {/* Mobile header */}
        {isMobile && (
          <div
            className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-3"
            style={{ background: 'linear-gradient(180deg, oklch(15% 0.012 250 / 0.8), transparent)' }}
          >
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg cursor-pointer"
              style={{
                background: 'oklch(15% 0.012 250 / 0.5)',
                color: 'var(--gs-ink-0)',
              }}
            >
              <Menu className="w-5 h-5" />
            </button>
            <CastPanel />
          </div>
        )}

        {/* Guide Toggle Bar */}
        <div
          className="flex items-center px-3 py-1.5 shrink-0"
          style={{
            background: 'var(--gs-glass-1)',
            borderBottom: '1px solid var(--gs-glass-stroke)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer"
            style={{
              background: showGuide ? 'var(--gs-accent-soft)' : 'oklch(82% 0.02 250 / 0.06)',
              border: `1px solid ${showGuide ? 'var(--gs-accent)' : 'var(--gs-glass-stroke)'}`,
              color: showGuide ? 'var(--gs-accent)' : 'var(--gs-ink-1)',
              fontFamily: 'inherit',
            }}
          >
            <List className="w-3.5 h-3.5" />
            Guide
          </button>

          {/* Mosaic info strip */}
          {effectiveLayout > 1 && (
            <div className="flex items-center gap-3 ml-3">
              <span
                className="gs-mono text-[10px] font-medium"
                style={{ letterSpacing: '0.12em', color: 'var(--gs-ink-2)' }}
              >
                {effectiveLayout === 2 ? '2×1' : '2×2'} MOSAIC
              </span>
              <span className="w-1 h-1 rounded-full" style={{ background: 'var(--gs-ink-3)' }} />
              <span
                className="gs-mono inline-flex items-center gap-1 text-[10px] font-medium"
                style={{ letterSpacing: '0.08em', color: 'var(--gs-accent)' }}
              >
                <Volume2 className="w-3 h-3" />
                AUDIO ON SLOT 1
              </span>
            </div>
          )}
        </div>

        {/* EPG Guide (collapsible) */}
        {showGuide && (
          <div
            className="shrink-0 p-2"
            style={{ height: 320, borderBottom: '1px solid var(--gs-glass-stroke)' }}
          >
            <EPGGuide
              channels={channels}
              onChannelSelect={handleChannelClick}
              isChannelPlaying={isChannelPlaying}
            />
          </div>
        )}

        {/* Video grid */}
        <div className={`grid ${getGridClasses()} flex-1 min-h-0 gap-2 p-2`}>
          {renderGrid()}
        </div>
      </main>
    </div>
  )
}
